import {type RunKeyInfoInfo} from '../../../types/run';
import {type PanelProps} from '../../../util/panelProps';
import * as Run from '../../../util/runs';
import {type Key as RunKey} from '../../../util/runTypes';
import {type ScatterAxisKey, type SemanticColorKey} from '../axisKeyTypes';
import {type ScatterPlotConfig} from '../common';

/** Parse an axis wire string (`summary:accuracy`) into `Run.Key`. */
export function parseColorFieldFromAxisKey(axisKey: string): RunKey | null {
  return Run.keyFromString(axisKey);
}

/**
 * Parse a semantic wire string (`accuracy`, `config.model.value`) into `Run.Key`.
 * Returns null for placeholders (`default`, empty) and unparseable strings.
 */
export function parseColorFieldFromSemanticKey(
  semanticKey: string
): RunKey | null {
  if (!isMeaningfulSemanticKey(semanticKey)) {
    return null;
  }
  if (semanticKey.startsWith('summary.')) {
    return Run.keyFromString(`summary:${semanticKey.slice('summary.'.length)}`);
  }
  if (semanticKey.startsWith('config.')) {
    return Run.keyFromString(`config:${semanticKey.slice('config.'.length)}`);
  }
  if (semanticKey.includes(':')) {
    return Run.keyFromString(semanticKey);
  }
  return Run.keyFromString(`summary:${semanticKey}`);
}

/** Serialize `Run.Key` to an axis wire string for `zAxis` and `Run.getValueSafe`. */
export function toAxisKeyString(key: RunKey): ScatterAxisKey {
  return Run.keyToString(key) as ScatterAxisKey;
}

/** Serialize `Run.Key` to a semantic wire string for `semanticLegendSettings.key`. */
export function toSemanticKeyString(key: RunKey): SemanticColorKey {
  if (key.section === 'config') {
    return `config.${key.name}` as SemanticColorKey;
  }
  return key.name as SemanticColorKey;
}

/** Both persisted wire views of the same color field. */
export function getColorFieldWireFormats(key: RunKey): {
  scatterAxisKey: ScatterAxisKey;
  semanticKey: SemanticColorKey;
} {
  return {
    scatterAxisKey: toAxisKeyString(key),
    semanticKey: toSemanticKeyString(key),
  };
}

/** GraphQL `metricName` for summary metric aggregation queries. */
export function toGraphQLMetricName(key: RunKey): string {
  return key.name;
}

/**
 * GraphQL `configKey` for config value aggregation queries.
 * Returns null when the field is not a config key.
 */
export function toGraphQLConfigKey(key: RunKey): string | null {
  if (key.section !== 'config') {
    return null;
  }
  return key.name;
}

export function isMeaningfulAxisKey(
  key: string | undefined
): key is ScatterAxisKey {
  return key != null && key !== '' && Run.keyStringDisplayName(key) !== '';
}

export function isMeaningfulSemanticKey(
  key: string | undefined
): key is SemanticColorKey {
  return key != null && key.trim() !== '' && key !== 'default';
}

/** Candidate strings for looking up a config field in aggregated `configKeyInfo`. */
export function getConfigKeyInfoLookupCandidates({
  keyString,
  semanticKey,
}: {
  keyString: string;
  semanticKey: string;
}): string[] {
  const candidates: string[] = [];
  const add = (candidate: string | undefined) => {
    if (candidate == null || candidate === '') {
      return;
    }
    if (!candidates.includes(candidate)) {
      candidates.push(candidate);
    }
  };

  add(semanticKey);
  add(keyString);

  const key = Run.keyFromString(keyString);
  if (key?.section !== 'config') {
    return candidates;
  }

  for (const possibility of Run.keyPossibilities(key)) {
    add(Run.keyToString(possibility));
    add(`config.${possibility.name}`);
  }

  add(Run.keyToString(key));
  add(`config.${key.name}`);

  if (key.name.includes('.value')) {
    const strippedName = key.name.replace('.value', '');
    add(Run.keyToString({section: 'config', name: strippedName}));
    add(`config.${strippedName}`);
  }

  return candidates;
}

export function resolveConfigKeyInfoEntry({
  configKeyInfo,
  option,
}: {
  configKeyInfo: PanelProps<ScatterPlotConfig>['data']['keyInfo'];
  option: {
    scatterAxisKey: ScatterAxisKey | string;
    semanticKey: SemanticColorKey | string;
  };
}): RunKeyInfoInfo | undefined {
  for (const candidate of getConfigKeyInfoLookupCandidates({
    keyString: option.scatterAxisKey,
    semanticKey: option.semanticKey,
  })) {
    const entry = configKeyInfo?.[candidate];
    if (entry != null) {
      return entry;
    }
  }
  return undefined;
}

export function getConfigKeyMajorType({
  configKeyInfo,
  option,
}: {
  configKeyInfo: PanelProps<ScatterPlotConfig>['data']['keyInfo'];
  option: {
    scatterAxisKey: ScatterAxisKey | string;
    semanticKey: SemanticColorKey | string;
  };
}): string | undefined {
  return resolveConfigKeyInfoEntry({configKeyInfo, option})?.majorType;
}
